import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Star, BadgeCheck, Phone, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { productCategories, getProductsByCategory, getProductImages, PLACEHOLDER_IMAGE } from '../data/products';

const PHONE_NUMBER = "+919971231812";
const WHATSAPP_NUMBER = "919971231812";

const categoryIds = ['pillows', 'cushions', 'bolsters', 'bedsheets', 'towels', 'mattresses', 'blankets', 'mats', 'tableMats', 'babyProducts', 'sofaProducts'];

// Product Card with Image
const ProductCard = ({ product, index }) => {
  const [imageError, setImageError] = useState(false);
  const productImages = getProductImages(product);
  const mainImage = imageError ? PLACEHOLDER_IMAGE : productImages[0];

  return (
    <Link 
      to={`/products/${product.id}`}
      className="block"
    >
      <div 
        data-testid={`product-card-${index}`}
        className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all border border-stone-100 group cursor-pointer"
      >
        {/* Product Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
          <img 
            src={mainImage}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImageError(true)}
            loading="lazy"
          />
          {product.bestseller && (
            <span className="absolute top-3 left-3 bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
              ⭐ Bestseller
            </span>
          )}
          {/* Image count badge */}
          {productImages.length > 1 && (
            <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
              +{productImages.length - 1} photos
            </span>
          )}
        </div>
        
        {/* Product Info */}
        <div className="p-5">
          <h3 className="font-bold text-stone-800 text-lg mb-2 group-hover:text-teal-700 transition-colors line-clamp-2">
            {product.name}
          </h3>
          <p className="text-stone-500 text-sm mb-1">Type: {product.type}</p>
          <p className="text-teal-600 text-sm font-medium">{product.brand}</p>
          
          {/* View Details Link */}
          <div className="mt-3 pt-3 border-t border-stone-100">
            <span className="text-teal-600 text-sm font-medium group-hover:underline">
              View Details →
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const [activeCategory, setActiveCategory] = useState(
    categoryIds.includes(categoryParam) ? categoryParam : 'mattresses'
  );
  
  const currentCategory = productCategories[activeCategory];
  const currentProducts = getProductsByCategory(activeCategory);

  // Update URL when category changes
  const handleCategoryChange = (catId) => {
    setActiveCategory(catId);
    setSearchParams({ category: catId });
  };

  // Sync with URL params
  useEffect(() => {
    if (categoryParam && categoryIds.includes(categoryParam)) {
      setActiveCategory(categoryParam);
    }
  }, [categoryParam]);

  return (
    <main data-testid="products-page" className="bg-[#FDFBF7]">
      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#FDFBF7] via-[#F0FDFA] to-[#CCFBF1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-medium tracking-widest uppercase text-teal-700 mb-3">
            Premium Collection
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-800 mb-6">
            Our Products
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Discover our wide range of premium mattresses, bedding, and home furnishing products 
            from trusted brands like Sleepwell, King Koil, Kurlon, and more.
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categoryIds.map((catId) => (
              <button
                key={catId}
                onClick={() => handleCategoryChange(catId)}
                data-testid={`tab-${catId}`}
                className={`px-6 py-3 rounded-full font-medium transition-colors ${
                  activeCategory === catId
                    ? 'bg-teal-600 text-white'
                    : 'bg-white border border-stone-200 text-stone-600 hover:border-teal-300'
                }`}
              >
                {productCategories[catId].name}
              </button>
            ))}
          </div>

          {/* Category Content */}
          {currentCategory && (
            <div id={activeCategory}>
              {/* Category Header */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                <div className="lg:col-span-1">
                  <div className="rounded-2xl overflow-hidden h-64 lg:h-full">
                    <img 
                      src={currentCategory.image}
                      alt={currentCategory.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="lg:col-span-2 flex flex-col justify-center">
                  <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
                    {currentCategory.name}
                  </h2>
                  <p className="text-lg text-stone-600 mb-4">
                    {currentCategory.description}
                  </p>
                  <p className="text-teal-600 font-medium mb-4">
                    {currentCategory.count}+ products available | Brands: {currentCategory.brands.join(', ')}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-stone-500">
                    <span className="flex items-center gap-2">
                      <BadgeCheck className="w-5 h-5 text-teal-600" />
                      100% Genuine Products
                    </span>
                    <span className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-500" />
                      Top Brands Available
                    </span>
                  </div>
                </div>
              </div>

              {/* Products Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {currentProducts.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 text-center bg-stone-50 rounded-3xl p-8 md:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              Can't find what you're looking for?
            </h3>
            <p className="text-stone-600 mb-6">
              Call us or visit our store for personalized assistance. We have many more products in-store!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${PHONE_NUMBER}`}>
                <Button 
                  size="lg"
                  className="rounded-full bg-teal-600 hover:bg-teal-700 w-full sm:w-auto"
                  data-testid="products-call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call +91 99712 31812
                </Button>
              </a>
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white w-full sm:w-auto"
                  data-testid="products-whatsapp-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProductsPage;
